import { CgOcrField } from './ocr/ocrResponseTypes';

/**
 * Journal de trace des phases d'analyse IA du flow /form-cgia.
 *
 * Objectif : que n'importe qui (agence, client, support) puisse ouvrir la
 * console navigateur, suivre PRÉCISÉMENT ce que fait le pipeline IA — quel
 * outil, quel modèle, quelles données lues, quelles décisions prises — et
 * copier le raisonnement complet pour le partager à un projet Claude externe
 * qui a accès aux mêmes pièces jointes du dossier carte grise.
 *
 * Toutes les lignes sont préfixées `[FormCGIA·IA]` et bufferisées en mémoire.
 * Dans la console :
 *   > __formCgiaIaTrace.dump()   // réimprime TOUT le journal en un bloc copiable
 *   > __formCgiaIaTrace.text()   // renvoie le journal en string brute
 */

const PREFIX = '[FormCGIA·IA]';

const traceBuffer: string[] = [];

/**
 * Logge une étape du pipeline IA, horodatée, et l'archive dans le buffer.
 * `payload` (optionnel) est sérialisé en JSON indenté pour rester copiable.
 */
export const traceIa = (phase: string, message: string, payload?: unknown): void => {
  const payloadText = payload === undefined ? '' : `\n${JSON.stringify(payload, null, 2)}`;
  const line = `${new Date().toISOString()} [${phase}] ${message}${payloadText}`;
  traceBuffer.push(line);
  console.log(`${PREFIX} [${phase}] ${message}${payloadText}`);
};

/** Une ligne lisible par champ OCR : valeur, confiance, source, lecture brute, alternatives. */
export const formatOcrField = (fieldKey: string, field: CgOcrField): string => {
  const confidencePct = Math.round((field.confidence ?? 0) * 100);
  const rawTextPart = field.rawText ? `, lu brut: "${field.rawText}"` : '';
  const alternatesPart =
    field.alternates && field.alternates.length > 0
      ? `, lectures alternatives plausibles: [${field.alternates.join(', ')}]`
      : '';
  return `  · ${fieldKey} = ${JSON.stringify(field.value)} (confiance ${confidencePct}%, source: ${field.source}${rawTextPart}${alternatesPart})`;
};

/** Tous les champs d'une réponse OCR, une ligne par champ. */
export const formatOcrFields = (fields: Record<string, CgOcrField>): string => {
  const entries = Object.entries(fields);
  if (entries.length === 0) return '  (aucun champ extrait)';
  return entries.map(([key, field]) => formatOcrField(key, field)).join('\n');
};

export const getIaTraceText = (): string => traceBuffer.join('\n\n');

/** Réimprime le journal complet en un seul bloc — pratique pour tout copier d'un coup. */
export const dumpIaTrace = (): void => {
  const bar = '═'.repeat(100);
  console.log(
    `${PREFIX}\n${bar}\n` +
      `JOURNAL COMPLET DES PHASES D'ANALYSE IA — /form-cgia (${traceBuffer.length} entrées)\n` +
      `À copier intégralement pour le partager à un projet Claude qui a les mêmes pièces jointes.\n` +
      `${bar}\n\n${getIaTraceText()}\n\n${bar}`,
  );
};

/**
 * Miroir frontend de `AuditBatchControllerResponse` (backend
 * `cgAuditBatchController.ts`) — réponse des endpoints d'audit batch Opus :
 * POST /api/cg/audit-batch/preflight et POST /api/cg/:id/audit-batch.
 */
export interface CgAuditBatchApiResponse {
  success: boolean;
  data?: {
    verdict: string;
    result: unknown;
    markdown: string | null;
    modelUsed: string;
    cached: boolean;
    promptTokens: number | null;
    completionTokens: number | null;
  };
  /**
   * Vrai en mode adoptOnly (soumission) quand aucun verdict pré-calculé
   * n'était disponible : aucun appel Claude n'a été payé.
   */
  pending?: boolean;
  error?: string;
}

// Types structurels minimaux (duck typing) pour éviter tout import circulaire
// avec le store et le moteur de cross-checks, qui importent eux-mêmes traceIa.
interface TraceIngestionItem {
  fileName: string;
  status: string;
  docTypeDetected?: string;
  confidence?: number;
  errorMessage?: string;
}

interface TraceUploadedDoc {
  docType: string;
  firebaseUrls: string[];
  ocrResponse: { docTypeMatchConfidence: number; warnings: string[]; fields: Record<string, CgOcrField> };
}

interface TraceExtractedFieldEntry {
  fieldKey: string;
  field: CgOcrField;
  sourceDocType: string;
}

interface TraceFinding {
  ruleId: string;
  severity: string;
  message: string;
}

interface GlobalIaReportArgs {
  regPlate: string | null;
  department: string | null;
  ingestionItems: Record<string, TraceIngestionItem>;
  uploadedDocs: Partial<Record<string, TraceUploadedDoc>>;
  extractedFields: Record<string, TraceExtractedFieldEntry>;
  findings: { errors: TraceFinding[]; warnings: TraceFinding[]; infos: TraceFinding[] };
}

/**
 * Rapport global consolidé du dossier, émis quand toutes les analyses IA d'un
 * batch sont terminées. C'est LE bloc à copier pour un second avis Claude :
 * fichiers traités, pièces reçues, champs fusionnés, cross-checks.
 */
export const logGlobalIaReport = (args: GlobalIaReportArgs): void => {
  const bar = '─'.repeat(100);

  const fileLines = Object.values(args.ingestionItems).map((item) => {
    const detected = item.docTypeDetected ? ` → type détecté: ${item.docTypeDetected}` : '';
    const confidence = typeof item.confidence === 'number' ? ` (confiance ${item.confidence}%)` : '';
    const errorPart = item.errorMessage ? ` — erreur: ${item.errorMessage}` : '';
    return `  · "${item.fileName}" — statut: ${item.status}${detected}${confidence}${errorPart}`;
  });

  const docLines = Object.values(args.uploadedDocs)
    .filter((doc): doc is TraceUploadedDoc => Boolean(doc))
    .map((doc) => {
      const confidencePct = Math.round(doc.ocrResponse.docTypeMatchConfidence * 100);
      const warningsPart =
        doc.ocrResponse.warnings.length > 0 ? `, warnings: [${doc.ocrResponse.warnings.join(', ')}]` : '';
      return `  · ${doc.docType} (confiance type ${confidencePct}%${warningsPart})\n${formatOcrFields(doc.ocrResponse.fields)}`;
    });

  const mergedLines = Object.values(args.extractedFields).map(
    (entry) =>
      `  · ${entry.fieldKey} = ${JSON.stringify(entry.field.value)} (confiance ${Math.round(
        (entry.field.confidence ?? 0) * 100,
      )}%, retenu depuis: ${entry.sourceDocType})`,
  );

  const formatFindings = (findings: TraceFinding[], emptyLabel: string): string =>
    findings.length === 0
      ? `  (${emptyLabel})`
      : findings.map((f) => `  · [${f.ruleId}] ${f.message}`).join('\n');

  const report = [
    bar,
    "RAPPORT GLOBAL D'ANALYSE IA — toutes les analyses du batch sont terminées.",
    'Copiez ce bloc (et/ou __formCgiaIaTrace.dump() pour le journal exhaustif) pour un second avis Claude.',
    bar,
    `Plaque saisie (étape 1): ${args.regPlate ?? '—'} — Département: ${args.department ?? '—'}`,
    '',
    `1) FICHIERS TRAITÉS (${Object.keys(args.ingestionItems).length}) :`,
    ...fileLines,
    '',
    `2) PIÈCES DU DOSSIER (${docLines.length}) — extraction par modèle de vision (cf. lignes [EXTRACTION] ci-dessus pour modèle exact et tokens) :`,
    ...docLines,
    '',
    `3) CHAMPS FUSIONNÉS (${mergedLines.length}) — meilleure source retenue (score = confiance × priorité du type de pièce) :`,
    ...mergedLines,
    '',
    `4) CROSS-CHECKS (moteur de règles métier frontend, sans IA) :`,
    `  BLOCAGES (${args.findings.errors.length}) :`,
    formatFindings(args.findings.errors, 'aucun blocage'),
    `  POINTS D'ATTENTION (${args.findings.warnings.length}) :`,
    formatFindings(args.findings.warnings, "aucun point d'attention"),
    `  INFOS (${args.findings.infos.length}) :`,
    formatFindings(args.findings.infos, 'aucune info'),
    bar,
  ].join('\n');

  traceIa('RAPPORT', report);
};

interface FormCgiaTraceGlobal {
  dump: () => void;
  text: () => string;
}

// Exposé en global pour récupérer le journal à la demande depuis la console.
// Garde `typeof window` : le module est aussi importé par les tests Vitest (node).
if (typeof window !== 'undefined') {
  (window as Window & { __formCgiaIaTrace?: FormCgiaTraceGlobal }).__formCgiaIaTrace = {
    dump: dumpIaTrace,
    text: getIaTraceText,
  };
}
